import isEmpty from "lodash/isEmpty";
import { Check, Pencil, Plus, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useGetTransportTypesList } from "../../../../api/cultivations";
import {
  useDeleteTransportDetails,
  useGetTransportList,
  usePostTransportData,
  usePutTransportDetails
} from "../../../../api/transportDetails";
import ConfirmModal from "../../../../components/confirmViewModal/ConfirmModal";
import { FormInput } from "../../../../components/ui/Input/FormInput/FormInput";
import { FormInputNumber } from "../../../../components/ui/Input/FormInputNumber/FormInputNumber";
import { FormSelect } from "../../../../components/ui/Input/FormSelector/FormSelect";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from "../../../../components/ui/common/accordion";
import { Button } from "../../../../components/ui/common/button";
import { Card } from "../../../../components/ui/common/card";
import { Form } from "../../../../components/ui/common/form";
import { Input } from "../../../../components/ui/common/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../../../../components/ui/common/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../../../../components/ui/common/table";
import { errorView, successMessage, warningMessage } from "../../../../helpers/ErrorHandler";
import { valueGraterThanZero } from "../../../../utils/utils";

interface TransportationSectionProps {
  ingredientId: string;
  supplierId: string;
  disabled?: boolean;
}

interface TransportFormValues {
  origin: string;
  deliveryPoint: string;
  transportationType: number;
  distance: number;
  weight: number;
}

const DEFAULT_VALUES: TransportFormValues = {
  origin: "",
  deliveryPoint: "",
  transportationType: 1,
  distance: 0,
  weight: 0
};

interface InlineTransportEditValues {
  origin: string;
  deliveryPoint: string;
  transportationType: number | string;
  distance: number | string;
  weight: number | string;
}

/**
 * Tailwind rebuild of the legacy `TransportationDetails`
 * (src/screens/ingredients/transportationDetails/TransportationDetails.tsx)
 * — same add/edit/delete transport-record behavior and API calls, only the
 * presentation moved off Semantic UI (`Accordion`/`Grid`/`Table`/`DropDown`)
 * onto the Tailwind equivalents (`common/accordion`, `common/table`,
 * `FormSelect`) and the common `ui/Input` field components.
 *
 * Unlike the legacy flow, editing an existing row no longer populates the
 * form above the table — clicking its pencil icon turns that row's own
 * cells into inputs (checkmark/X to save/cancel), so the add form and its
 * validation stay untouched by an in-progress edit; the top form is add-only.
 */
const TransportationSection = ({
  ingredientId,
  supplierId,
  disabled = false
}: TransportationSectionProps) => {
  const { data: transportTypeList } = useGetTransportTypesList();
  const [visibleDeleteModal, setVisibleDeleteModal] = useState(false);
  const [selectData, setSelectData] = useState<any>(null);
  const [accordionValue, setAccordionValue] = useState<string>("");
  const [inlineEditId, setInlineEditId] = useState<string | null>(null);
  const [inlineValues, setInlineValues] = useState<InlineTransportEditValues>({
    origin: "",
    deliveryPoint: "",
    transportationType: "",
    distance: "",
    weight: ""
  });

  const methods = useForm<TransportFormValues>({ defaultValues: DEFAULT_VALUES });
  const { handleSubmit, control, setValue } = methods;

  const { data: transportData, refetch: transportDetails } = useGetTransportList(
    ingredientId,
    supplierId
  );

  useEffect(() => {
    const hasTransportData = transportData && transportData.length > 0;
    setAccordionValue(hasTransportData || disabled ? "content" : "");
  }, [transportData, disabled]);

  const { mutate: postTransportDetails } = usePostTransportData();
  const { mutate: putTransportDetails } = usePutTransportDetails();
  const { mutate: deleteTransportData } = useDeleteTransportDetails();

  const resetTransportData = () => {
    transportDetails();
    setValue("origin", DEFAULT_VALUES.origin);
    setValue("deliveryPoint", DEFAULT_VALUES.deliveryPoint);
    setValue("distance", DEFAULT_VALUES.distance);
    setValue("weight", DEFAULT_VALUES.weight);
    setValue("transportationType", DEFAULT_VALUES.transportationType);
  };

  // Only reachable from the add form now — editing an existing row goes
  // through `onSaveInlineEdit` instead (see the file doc comment).
  const saveTransportData = (data: TransportFormValues) => {
    const payload: Record<string, unknown> = { ...data, ingredientId, supplierId };

    postTransportDetails(payload, {
      onSuccess() {
        successMessage("Transport Details Save Successfully");
        transportDetails();
        resetTransportData();
      }
    });
  };

  const onStartInlineEdit = (data: any) => {
    setInlineEditId(data._id);
    setInlineValues({
      origin: data?.origin || "",
      deliveryPoint: data?.deliveryPoint || "",
      transportationType: data?.transportationType ?? "",
      distance: data?.distance ?? "",
      weight: data?.weight ?? ""
    });
  };

  const onCancelInlineEdit = () => {
    setInlineEditId(null);
    setInlineValues({ origin: "", deliveryPoint: "", transportationType: "", distance: "", weight: "" });
  };

  const onSaveInlineEdit = (data: any) => {
    if (!inlineValues.origin.trim()) {
      errorView("From is required");
      return;
    }
    if (!inlineValues.deliveryPoint.trim()) {
      errorView("To is required");
      return;
    }
    if (!inlineValues.transportationType) {
      errorView("Transportation Type is required");
      return;
    }
    if (!valueGraterThanZero(inlineValues.distance)) {
      errorView("Distance must be grater than 0");
      return;
    }
    if (!valueGraterThanZero(inlineValues.weight)) {
      errorView("Weight must be grater than 0");
      return;
    }

    const payload: Record<string, unknown> = {
      origin: inlineValues.origin,
      deliveryPoint: inlineValues.deliveryPoint,
      transportationType: Number(inlineValues.transportationType),
      distance: Number(inlineValues.distance),
      weight: Number(inlineValues.weight),
      ingredientId,
      supplierId,
      ingredientTransportId: data._id
    };

    putTransportDetails(payload, {
      onSuccess() {
        successMessage("Transport Details Updated Successfully");
        transportDetails();
        onCancelInlineEdit();
      }
    });
  };

  return (
    <Card className="tw-gap-3 tw-py-4">
      <Accordion
        type="single"
        collapsible
        value={accordionValue}
        onValueChange={value => {
          if (isEmpty(ingredientId)) {
            warningMessage("Please create Ingredient");
            return;
          }
          setAccordionValue(value);
        }}
      >
        <AccordionItem value="content" className="tw-border-b-0">
          <AccordionTrigger className="tw-px-4 tw-pt-1 tw-pb-3 hover:tw-no-underline">
            <span className="tw-text-sm tw-font-semibold tw-text-primary">Transportation Details</span>
          </AccordionTrigger>
          <AccordionContent className="tw-px-4">
            <Form {...methods}>
              {!disabled ? (
                <div className="tw-mb-4 tw-grid tw-grid-cols-2 tw-items-start tw-gap-3 md:tw-grid-cols-[1fr_1fr_1fr_1fr_1fr_auto]">
                  <FormInput
                    control={control}
                    name="origin"
                    label="From"
                    placeholder="From"
                    required
                    errorMessage="From is required"
                  />
                  <FormInput
                    control={control}
                    name="deliveryPoint"
                    label="To"
                    placeholder="To"
                    required
                    errorMessage="To is required"
                  />
                  <FormSelect
                    control={control}
                    name="transportationType"
                    label="Transportation Type"
                    options={transportTypeList || []}
                    placeholder="Select Transportation Type"
                    required
                    onChange={value => setValue("transportationType", Number(value))}
                  />
                  <FormInputNumber
                    control={control}
                    name="distance"
                    label="Distance"
                    placeholder="0.00"
                    required
                    errorMessage="Distance is required"
                  />
                  <FormInputNumber
                    control={control}
                    name="weight"
                    label="Weight (kg)"
                    placeholder="0.00"
                    required
                    errorMessage="Weight is required"
                  />
                  <div className="tw-flex tw-flex-col tw-gap-2">
                    <span
                      aria-hidden="true"
                      className="tw-invisible tw-text-sm tw-font-medium tw-leading-none"
                    >
                      &nbsp;
                    </span>
                    <Button
                      type="button"
                      size="icon"
                      className="tw-h-10 tw-w-10 tw-justify-self-start"
                      onClick={handleSubmit(data => saveTransportData(data))}
                    >
                      <Plus className="tw-h-5 tw-w-5" />
                    </Button>
                  </div>
                </div>
              ) : null}
            </Form>

            {transportData?.length > 0 ? (
              <div className="tw-overflow-x-auto tw-rounded-md tw-border tw-border-border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>From</TableHead>
                      <TableHead>To</TableHead>
                      <TableHead>Transportation type</TableHead>
                      <TableHead>Distance</TableHead>
                      <TableHead>Weight</TableHead>
                      <TableHead className="tw-text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transportData.map((data: any) => {
                      const transportationType = transportTypeList?.find(
                        // eslint-disable-next-line eqeqeq -- loose match preserved from TransportationDetails.tsx
                        (e: any) => e.value == data.transportationType
                      );
                      const isEditingRow = inlineEditId === data._id;
                      return (
                        <TableRow key={data._id}>
                          <TableCell>
                            {isEditingRow ? (
                              <Input
                                className="tw-h-8"
                                value={inlineValues.origin}
                                onChange={e =>
                                  setInlineValues(prev => ({ ...prev, origin: e.target.value }))
                                }
                              />
                            ) : (
                              data?.origin ?? ""
                            )}
                          </TableCell>
                          <TableCell>
                            {isEditingRow ? (
                              <Input
                                className="tw-h-8"
                                value={inlineValues.deliveryPoint}
                                onChange={e =>
                                  setInlineValues(prev => ({ ...prev, deliveryPoint: e.target.value }))
                                }
                              />
                            ) : (
                              data?.deliveryPoint ?? ""
                            )}
                          </TableCell>
                          <TableCell>
                            {isEditingRow ? (
                              <Select
                                value={String(inlineValues.transportationType)}
                                onValueChange={value =>
                                  setInlineValues(prev => ({ ...prev, transportationType: value }))
                                }
                              >
                                <SelectTrigger className="tw-h-8 tw-w-full">
                                  <SelectValue placeholder="Transportation Type" />
                                </SelectTrigger>
                                <SelectContent>
                                  {transportTypeList?.map((option: any) => (
                                    <SelectItem key={option.value} value={String(option.value)}>
                                      {option.text}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              transportationType?.text ?? ""
                            )}
                          </TableCell>
                          <TableCell>
                            {isEditingRow ? (
                              <Input
                                type="number"
                                className="tw-h-8"
                                value={inlineValues.distance}
                                onChange={e =>
                                  setInlineValues(prev => ({ ...prev, distance: e.target.value }))
                                }
                              />
                            ) : (
                              data?.distance ?? 0
                            )}
                          </TableCell>
                          <TableCell>
                            {isEditingRow ? (
                              <Input
                                type="number"
                                className="tw-h-8"
                                value={inlineValues.weight}
                                onChange={e =>
                                  setInlineValues(prev => ({ ...prev, weight: e.target.value }))
                                }
                              />
                            ) : (
                              data?.weight ?? 0
                            )}
                          </TableCell>
                          <TableCell className="tw-text-right">
                            {!disabled ? (
                              <div className="tw-flex tw-justify-end tw-gap-3">
                                {isEditingRow ? (
                                  <>
                                    <button type="button" onClick={() => onSaveInlineEdit(data)}>
                                      <Check className="tw-h-4 tw-w-4 tw-text-primary" />
                                    </button>
                                    <button type="button" onClick={onCancelInlineEdit}>
                                      <X className="tw-h-4 tw-w-4 tw-text-destructive" />
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button type="button" onClick={() => onStartInlineEdit(data)}>
                                      <Pencil className="tw-h-4 tw-w-4 tw-text-muted-foreground" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setSelectData(data);
                                        setVisibleDeleteModal(true);
                                      }}
                                    >
                                      <Trash2 className="tw-h-4 tw-w-4 tw-text-muted-foreground" />
                                    </button>
                                  </>
                                )}
                              </div>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p className="tw-text-sm tw-text-muted-foreground">
                Transport Details is not available
              </p>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <ConfirmModal
        viewModal={visibleDeleteModal}
        closeModal={() => setVisibleDeleteModal(false)}
        cancel={() => {
          setSelectData(null);
          setVisibleDeleteModal(false);
        }}
        approve={() => {
          deleteTransportData(selectData?._id, {
            onSuccess() {
              setSelectData(null);
              setVisibleDeleteModal(false);
              successMessage("Transport Details Deleted Successfully");
              transportDetails();
            }
          });
        }}
        title="Delete Transport Data"
        subTitle="Are you sure you want to delete transport data?"
      />
    </Card>
  );
};

export default TransportationSection;
